import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Heading2, Italic, Link2, List, ListOrdered, Quote, Redo2, Undo2 } from 'lucide-react';
import { type ReactNode, useEffect } from 'react';
import { t } from '@/lib/i18n';

/** HTML rich-text editor for news articles (the app renders the HTML). */
export function RichText({
  value,
  onChange,
  invalid,
}: {
  value: string;
  onChange: (html: string) => void;
  invalid?: boolean;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: t('Write the article…') }),
    ],
    content: value,
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
    editorProps: { attributes: { class: 'prose-editor min-h-60 px-3 py-2 text-sm' } },
  });
  // Load content that arrives after the editor was created (e.g. an article fetched from the API).
  useEffect(() => {
    if (editor && !editor.isFocused && value !== (editor.isEmpty ? '' : editor.getHTML())) {
      editor.commands.setContent(value, false);
    }
  }, [editor, value]);
  if (!editor) return null;
  const tool = (label: string, icon: ReactNode, active: boolean, run: () => void) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      // Keep focus (and the cursor) in the editor while using the toolbar.
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
      className={`rounded p-1.5 ${active ? 'bg-slate-200 text-slate-900' : 'text-slate-600 hover:bg-slate-100'}`}
    >
      {icon}
    </button>
  );
  const c = () => editor.chain().focus();
  return (
    <div
      className={`rounded-md border bg-white shadow-sm ${invalid ? 'border-red-400' : 'border-slate-300'}`}
    >
      <div className="flex flex-wrap gap-0.5 border-b border-slate-200 p-1">
        {tool(t('Bold'), <Bold className="size-4" />, editor.isActive('bold'), () => c().toggleBold().run())}
        {tool(t('Italic'), <Italic className="size-4" />, editor.isActive('italic'), () =>
          c().toggleItalic().run(),
        )}
        {tool(
          t('Section heading'),
          <Heading2 className="size-4" />,
          editor.isActive('heading', { level: 2 }),
          () => c().toggleHeading({ level: 2 }).run(),
        )}
        {tool(t('Bullet list'), <List className="size-4" />, editor.isActive('bulletList'), () =>
          c().toggleBulletList().run(),
        )}
        {tool(t('Numbered list'), <ListOrdered className="size-4" />, editor.isActive('orderedList'), () =>
          c().toggleOrderedList().run(),
        )}
        {tool(t('Quote'), <Quote className="size-4" />, editor.isActive('blockquote'), () =>
          c().toggleBlockquote().run(),
        )}
        {tool(t('Link'), <Link2 className="size-4" />, editor.isActive('link'), () => {
          const url = window.prompt(t('Link URL'), editor.getAttributes('link').href ?? 'https://');
          if (url === null) return;
          if (url === '') c().unsetLink().run();
          else c().extendMarkRange('link').setLink({ href: url }).run();
        })}
        <span className="mx-1 w-px bg-slate-200" />
        {tool(t('Undo'), <Undo2 className="size-4" />, false, () => c().undo().run())}
        {tool(t('Redo'), <Redo2 className="size-4" />, false, () => c().redo().run())}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
